import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { EntryApiAdapter } from '../../adapters/EntryApiAdapter';
import { CartApiAdapter } from '../adapters/CartApiAdapter';
import { ReceiptApiAdapter, RejectProductRequest } from '../adapters/ReceiptApiAdapter';
import { EntryProductInfo, ReceiveFlowState, RejectionData, ScannedProduct } from '../models/Receive';

export const useReceiveFlow = (entryId: number, receiptId: number) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const cartAdapter = useMemo(() => new CartApiAdapter(httpClient), [httpClient]);
  const receiptAdapter = useMemo(() => new ReceiptApiAdapter(httpClient), [httpClient]);
  const entryAdapter = useMemo(() => new EntryApiAdapter(httpClient), [httpClient]);
  const queryClient = useQueryClient();

  // Estado del flujo
  const [state, setState] = useState<ReceiveFlowState>({
    currentStep: 'cart',
    validatedCart: null,
    isProcessing: false,
    error: null,
  });

  // Productos escaneados: key = productId
  const [scannedProducts, setScannedProducts] = useState<Record<number, ScannedProduct>>({});

  // Producto pendiente de procesar (escaneado pero sin cantidad establecida)
  const [pendingProduct, setPendingProduct] = useState<import('../models/Receive').PendingProduct | null>(null);

  // Estado de recepción en curso
  const [isReceiving, setIsReceiving] = useState(false);

  // Query para obtener la entrada con sus líneas
  const {
    data: entry,
    isLoading: isLoadingEntry,
    error: entryError,
    refetch: refetchEntry,
  } = useQuery({
    queryKey: ['entry', entryId],
    queryFn: () => entryAdapter.getEntry(entryId),
    enabled: entryId > 0,
  });

  const entryLines = entry?.entry_lines || [];

  // Paso 1: Validar carrito por código escaneado
  const validateCart = useCallback(
    async (code: string): Promise<{ success: boolean; message: string }> => {
      setState((prev) => ({ ...prev, isProcessing: true, error: null }));

      try {
        const cart = await cartAdapter.searchByCode(code.trim());

        if (!cart) {
          setState((prev) => ({
            ...prev,
            isProcessing: false,
            error: `No se encontró ningún carrito con el código "${code}"`,
          }));
          return {
            success: false,
            message: `Carrito con código "${code}" no encontrado`,
          };
        }

        // Validar que el carrito esté libre
        if (cart.is_locked) {
          setState((prev) => ({
            ...prev,
            isProcessing: false,
            error: `El carrito "${cart.code}" está bloqueado`,
          }));
          return {
            success: false,
            message: `El carrito "${cart.code}" está bloqueado`,
          };
        }

        // Validar que el carrito no tenga productos de otra entrada
        // El carrito puede estar vacío o tener productos solo de la entrada actual
        const stockLocationCarts = cart.stock_location_carts || [];

        if (stockLocationCarts.length > 0) {
          // Verificar si el carrito está siendo usado en cualquier otra operación
          const isUsedElsewhere = stockLocationCarts.some((item) => {
            // Es inválido si:
            // 1. Tiene un entry_id distinto al actual
            // 2. O tiene cualquier otro ID de operación (preparación, devolución, etc.)
            return (
              (item.entry_id !== null && item.entry_id !== entryId) ||
              item.preparation_id !== null ||
              item.returned_reception_id !== null ||
              item.merchandise_id !== null ||
              item.location_order_id !== null ||
              (item as any).extra_work_order_id !== null
            );
          });

          if (isUsedElsewhere) {
            const errorMsg = `El carrito "${cart.code}" está siendo usado en otra operación.`;

            setState((prev) => ({
              ...prev,
              isProcessing: false,
              error: errorMsg,
            }));
            return {
              success: false,
              message: errorMsg,
            };
          }
        }

        // Carrito válido - avanzar al paso de productos
        setState((prev) => ({
          ...prev,
          isProcessing: false,
          validatedCart: cart,
          currentStep: 'products',
          error: null,
        }));

        return {
          success: true,
          message: `Carrito "${cart.code}" validado correctamente`,
        };
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Error al validar el carrito';

        setState((prev) => ({
          ...prev,
          isProcessing: false,
          error: errorMessage,
        }));

        return {
          success: false,
          message: errorMessage,
        };
      }
    },
    [cartAdapter, receiptId],
  );

  // Paso 2: Escanear producto de la entrada (solo identifica, no establece cantidad)
  const scanProduct = useCallback(
    async (code: string): Promise<{ success: boolean; message: string }> => {
      if (!entry) {
        return {
          success: false,
          message: 'No se ha cargado la información de la entrada',
        };
      }

      // Buscar producto por código en las líneas de la entrada
      const entryLine = entryLines.find((line) => line.product?.code?.toLowerCase() === code.toLowerCase().trim());

      if (!entryLine) {
        return {
          success: false,
          message: `El producto con código "${code}" no está en esta entrada`,
        };
      }

      const productId = entryLine.product_id;

      // Calcular pendiente real a nivel producto (igual que en EntryProductsList):
      // suma de (expected - accepted - rejected) de todas las líneas de este producto
      const linesForProduct = entryLines.filter((line) => line.product_id === productId);
      const totalExpected = linesForProduct.reduce((sum, l) => sum + l.expected_qty, 0);
      const totalAccepted = linesForProduct.reduce((sum, l) => sum + (l.qty_accept || 0), 0);
      const totalRejected = linesForProduct.reduce((sum, l) => sum + (l.qty_rejects || 0), 0);
      const pendingFromServer = totalExpected - totalAccepted - totalRejected;

      // Limitar por lo que el usuario tiene "en curso" (escaneado pero no confirmado)
      const existingScanned = scannedProducts[productId];
      const alreadyScannedQty = existingScanned?.scannedQty || 0;
      const maxAvailableQty = Math.max(0, pendingFromServer - alreadyScannedQty);

      // Validar que aún haya cantidad disponible
      if (maxAvailableQty <= 0) {
        return {
          success: false,
          message: `Ya se ha completado la cantidad para este producto (${totalAccepted + totalRejected}/${totalExpected})`,
        };
      }

      // Establecer producto pendiente para establecer cantidad
      setPendingProduct({
        productId,
        productCode: entryLine.product?.code || code,
        productName: entryLine.product?.name || `Producto ${productId}`,
        entryLineId: entryLine.id,
        expectedQty: entryLine.expected_qty,
        maxAvailableQty,
      });

      return {
        success: true,
        message: `Producto "${entryLine.product?.name || code}" identificado. Establece la cantidad.`,
      };
    },
    [entry, entryLines, scannedProducts],
  );

  // Aceptar producto
  const acceptProduct = useCallback(
    async (
      productId: number,
      locationCode: string,
      qty: number,
      batchId?: number,
    ): Promise<{ success: boolean; message: string }> => {
      if (!state.validatedCart) {
        return {
          success: false,
          message: 'No hay carrito validado',
        };
      }

      setIsReceiving(true);

      try {
        await receiptAdapter.acceptProduct(entryId, receiptId, {
          product_id: productId,
          location_code: locationCode,
          qty,
          ...(batchId && { batch_id: batchId }),
        });

        // Limpiar cantidad escaneada del producto
        setScannedProducts((prev) => {
          const updated = { ...prev };
          const scanned = updated[productId];
          if (scanned) {
            const newScannedQty = scanned.scannedQty - qty;
            if (newScannedQty <= 0) {
              delete updated[productId];
            } else {
              updated[productId] = {
                ...scanned,
                scannedQty: newScannedQty,
              };
            }
          }
          return updated;
        });

        // Invalidar queries
        queryClient.invalidateQueries({ queryKey: ['entry-receipt-movements', entryId, receiptId] });
        queryClient.invalidateQueries({ queryKey: ['entry-receipt-pending-locations', entryId, receiptId] });
        queryClient.invalidateQueries({ queryKey: ['entry', entryId] });

        // Actualizar carrito
        if (state.validatedCart.code) {
          try {
            const updatedCart = await cartAdapter.searchByCode(state.validatedCart.code);
            if (updatedCart) {
              setState((prev) => ({
                ...prev,
                validatedCart: updatedCart,
              }));
            }
          } catch (error) {
            console.error('Error al actualizar el carrito:', error);
          }
        }

        setIsReceiving(false);

        return {
          success: true,
          message: `Producto aceptado correctamente`,
        };
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Error al aceptar el producto';

        setIsReceiving(false);

        return {
          success: false,
          message: errorMessage,
        };
      }
    },
    [entryId, receiptId, state.validatedCart, receiptAdapter, cartAdapter, queryClient],
  );

  // Rechazar producto
  const rejectProduct = useCallback(
    async (rejectionData: RejectionData): Promise<{ success: boolean; message: string }> => {
      if (!state.validatedCart) {
        return {
          success: false,
          message: 'No hay carrito validado',
        };
      }

      setIsReceiving(true);

      try {
        const requestData: RejectProductRequest = {
          product_id: rejectionData.productId,
          qty: rejectionData.qty,
          rejection_type: rejectionData.rejectionType,
          rejection_text: rejectionData.rejectionText,
          attachments: rejectionData.attachments,
        };

        await receiptAdapter.rejectProduct(entryId, receiptId, requestData);

        // Limpiar cantidad escaneada del producto
        setScannedProducts((prev) => {
          const updated = { ...prev };
          const scanned = updated[rejectionData.productId];
          if (scanned) {
            const newScannedQty = scanned.scannedQty - rejectionData.qty;
            if (newScannedQty <= 0) {
              delete updated[rejectionData.productId];
            } else {
              updated[rejectionData.productId] = {
                ...scanned,
                scannedQty: newScannedQty,
              };
            }
          }
          return updated;
        });

        // Invalidar queries
        queryClient.invalidateQueries({ queryKey: ['entry-receipt-movements', entryId, receiptId] });
        queryClient.invalidateQueries({ queryKey: ['entry', entryId] });

        setIsReceiving(false);

        return {
          success: true,
          message: `Producto rechazado correctamente`,
        };
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Error al rechazar el producto';

        setIsReceiving(false);

        return {
          success: false,
          message: errorMessage,
        };
      }
    },
    [entryId, receiptId, state.validatedCart, receiptAdapter, queryClient],
  );

  // Despachar carrito
  const dispatchCart = useCallback(
    async (
      locationId: number,
      dispatchType: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER',
      lines?: Array<{
        group_code: string;
        product_id: number;
        qty_expected: number;
        work_type: 'LABELING' | 'BAGGING' | 'BOTH';
      }>,
    ): Promise<{ success: boolean; message: string }> => {
      setIsReceiving(true);

      try {
        const payload: any = {
          location_id: locationId,
          dispatch_type: dispatchType,
        };

        if (dispatchType === 'EXTRA_WORK_ORDER' && lines) {
          payload.lines = lines;
        }

        await receiptAdapter.dispatchByLocation(entryId, receiptId, payload);

        // Invalidar queries
        queryClient.invalidateQueries({ queryKey: ['entry-receipt-movements', entryId, receiptId] });
        queryClient.invalidateQueries({ queryKey: ['entry-receipt-pending-locations', entryId, receiptId] });
        queryClient.invalidateQueries({ queryKey: ['entry', entryId] });

        // Volver al paso de carrito
        setState((prev) => ({
          ...prev,
          validatedCart: null,
          currentStep: 'cart',
        }));

        setIsReceiving(false);

        return {
          success: true,
          message: `Carrito despachado correctamente`,
        };
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Error al despachar el carrito';

        setIsReceiving(false);

        return {
          success: false,
          message: errorMessage,
        };
      }
    },
    [entryId, receiptId, receiptAdapter, queryClient],
  );

  // Finalizar recepción
  const completeReceipt = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    setIsReceiving(true);

    try {
      await receiptAdapter.completeReceipt(entryId, receiptId);

      // Invalidar queries
      queryClient.invalidateQueries({ queryKey: ['entry-receipt', entryId, receiptId] });
      queryClient.invalidateQueries({ queryKey: ['entry', entryId] });
      queryClient.invalidateQueries({ queryKey: ['entries'] });

      setIsReceiving(false);

      return {
        success: true,
        message: `Recepción finalizada correctamente`,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Error al finalizar la recepción';

      setIsReceiving(false);

      return {
        success: false,
        message: errorMessage,
      };
    }
  }, [entryId, receiptId, receiptAdapter, queryClient]);

  // Cancelar recepción
  const cancelReceipt = useCallback(
    async (cancelReason: string): Promise<{ success: boolean; message: string }> => {
      setIsReceiving(true);

      try {
        await receiptAdapter.cancelReceipt(entryId, receiptId, { cancel_reason: cancelReason });

        // Invalidar queries
        queryClient.invalidateQueries({ queryKey: ['entry-receipt', entryId, receiptId] });
        queryClient.invalidateQueries({ queryKey: ['entry', entryId] });
        queryClient.invalidateQueries({ queryKey: ['entries'] });

        setIsReceiving(false);

        return {
          success: true,
          message: `Recepción cancelada correctamente`,
        };
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Error al cancelar la recepción';

        setIsReceiving(false);

        return {
          success: false,
          message: errorMessage,
        };
      }
    },
    [entryId, receiptId, receiptAdapter, queryClient],
  );

  // Procesar escaneo según el paso actual
  const processScan = useCallback(
    async (code: string): Promise<{ success: boolean; message: string }> => {
      if (state.currentStep === 'cart') {
        return validateCart(code);
      }
      if (state.currentStep === 'products') {
        return scanProduct(code);
      }
      return { success: false, message: 'Paso no válido para escaneo' };
    },
    [state.currentStep, validateCart, scanProduct],
  );

  // Limpiar error
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Establecer cantidad para producto pendiente
  const setProductQuantity = useCallback(
    (qty: number) => {
      if (!pendingProduct) return;

      // Validar cantidad
      if (qty <= 0 || qty > pendingProduct.maxAvailableQty) {
        return;
      }

      // Agregar a productos escaneados
      const existingScanned = scannedProducts[pendingProduct.productId];
      const newScannedQty = (existingScanned?.scannedQty || 0) + qty;

      setScannedProducts((prev) => ({
        ...prev,
        [pendingProduct.productId]: {
          productId: pendingProduct.productId,
          productCode: pendingProduct.productCode,
          productName: pendingProduct.productName,
          entryLineId: pendingProduct.entryLineId,
          expectedQty: pendingProduct.expectedQty,
          scannedQty: newScannedQty,
        },
      }));

      // Limpiar producto pendiente
      setPendingProduct(null);
    },
    [pendingProduct, scannedProducts],
  );

  // Cancelar producto pendiente
  const cancelPendingProduct = useCallback(() => {
    setPendingProduct(null);
  }, []);

  // Volver al paso anterior
  const goBack = useCallback(() => {
    setState((prev) => {
      if (prev.currentStep === 'products') {
        return { ...prev, currentStep: 'cart', validatedCart: null };
      }
      return prev;
    });
    setScannedProducts({});
    setPendingProduct(null);
  }, []);

  // Obtener productos de entrada con información de escaneo
  const entryProducts: EntryProductInfo[] = useMemo(() => {
    return entryLines.map((line) => {
      const scanned = scannedProducts[line.product_id];
      return {
        ...line,
        product: line.product
          ? {
              id: line.product.id,
              name: line.product.name,
              code: line.product.code,
              image_hash: line.product.image_hash || undefined,
              img: line.product.img || undefined,
            }
          : undefined,
        scannedQty: scanned?.scannedQty || 0,
      };
    });
  }, [entryLines, scannedProducts]);

  return {
    // Estado
    entry,
    entryLines,
    entryProducts,
    isLoadingEntry,
    entryError,
    refetchEntry,

    // Estado del flujo
    state,
    scannedProducts,
    pendingProduct,
    isReceiving,

    // Funciones
    validateCart,
    scanProduct,
    setProductQuantity,
    cancelPendingProduct,
    acceptProduct,
    rejectProduct,
    dispatchCart,
    completeReceipt,
    cancelReceipt,
    processScan,
    clearError,
    goBack,
  };
};
